import { createFileRoute, Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { ChevronDown, FileText } from "lucide-react";
import { useReportActions, useReports } from "@/api/reports";
import { Page, PageHeader } from "@/components/shell/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { RelativeTime } from "@/components/common/RelativeTime";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { humanDate } from "@/lib/labels";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_app/reports/")({ component: ReportsPage });

function ReportsPage() {
  const reports = useReports();
  const { generate } = useReportActions();
  const run = (kind: "weekly" | "monthly" | "adhoc") =>
    generate
      .mutateAsync({ kind })
      .then((r) => toast.success(r.headline ?? "Report ready"))
      .catch((e: Error) => toast.error(e.message));

  return (
    <>
      <PageHeader
        title="Reports"
        subtitle="Weekly every Monday, monthly on the 1st. Or generate one now."
        actions={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={generate.isPending}>
                {generate.isPending ? "Generating..." : "Generate"} <ChevronDown className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => run("adhoc")}>Last 7 days to today</DropdownMenuItem>
              <DropdownMenuItem onClick={() => run("weekly")}>Last full week</DropdownMenuItem>
              <DropdownMenuItem onClick={() => run("monthly")}>Last full month</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
      />
      <Page>
        {reports.data?.length === 0 ? (
          <EmptyState title="No reports yet" hint="The first weekly report arrives next Monday morning, or generate one now." />
        ) : (
          <ul className="divide-y rounded-lg border bg-card">
            {reports.data?.map((r) => (
              <li key={r.id}>
                <Link to="/reports/$reportId" params={{ reportId: r.id }} className="flex items-center gap-3 px-3 py-2.5 text-sm hover:bg-muted/40">
                  <FileText className={cn("size-4 shrink-0", r.read ? "text-muted-foreground" : "text-primary")} />
                  <div className="min-w-0 flex-1">
                    <div className={cn("truncate", !r.read && "font-semibold")}>{r.headline ?? (r.status === "failed" ? "Failed" : "Generating...")}</div>
                    <div className="text-xs text-muted-foreground">
                      {r.kind} · {humanDate(r.period_start)} to {humanDate(r.period_end)}
                      {r.generated_at && (
                        <>
                          {" "}
                          · <RelativeTime iso={r.generated_at} />
                        </>
                      )}
                    </div>
                  </div>
                  {!r.read && <span className="size-2 rounded-full bg-primary" />}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Page>
    </>
  );
}
